import { FBMessage } from '@/types/fb-type';
import { WsEvent } from '@/types/ws-type';

type UnknownObject = Record<string, any>;

export function normalizeFbMessage(payload: unknown): FBMessage | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as UnknownObject;
  const rawMessage = source.message;
  const text =
    typeof rawMessage === 'string'
      ? rawMessage
      : typeof rawMessage?.text === 'string'
        ? rawMessage.text
        : '';

  const id =
    source.id ??
    rawMessage?.mid ??
    `${source.sender?.id ?? source.from?.id ?? 'unknown'}-${source.timestamp ?? Date.now()}`;

  const createdTime =
    typeof source.createdTime === 'string'
      ? source.createdTime
      : typeof source.created_time === 'string'
        ? source.created_time
        : source.timestamp
          ? new Date(Number(source.timestamp)).toISOString()
          : new Date().toISOString();

  // Resolve the sticker field: the backend sets this from `sticker_id` (numeric string),
  // not a URL. The actual image URL arrives inside `message.attachments[].url`.
  // Priority: source-level URL → rawMessage sticker URL → attachment URL → sticker_id CDN fallback
  const rawSticker = source.sticker ?? rawMessage?.sticker ?? '';
  const isNumericId = typeof rawSticker === 'string' && /^\d+$/.test(rawSticker);

  let resolvedSticker = '';
  if (rawSticker) {
    if (!isNumericId) {
      // Already a URL
      resolvedSticker = rawSticker;
    } else {
      // Try to extract the image URL from the first attachment
      const rawAttachments: unknown[] =
        source.attachments ?? rawMessage?.attachments ?? [];
      const firstAtt = rawAttachments[0] as Record<string, any> | undefined;
      const attUrl =
        firstAtt?.url ??
        firstAtt?.fileUrl ??
        firstAtt?.file_url ??
        firstAtt?.imageData?.url ??
        firstAtt?.image_data?.url ??
        '';
      if (attUrl) {
        resolvedSticker = attUrl;
      } else {
        // Last-resort: construct a known Facebook sticker CDN URL
        resolvedSticker = `https://external.xx.fbcdn.net/images/?mid=${rawSticker}`;
      }
    }
  }

  const rawAttachments: any[] = source.attachments ?? rawMessage?.attachments ?? [];
  const seenAttachmentKeys = new Set<string>();
  const normalizedAttachments = rawAttachments
    .map((att: any) => {
      if (!att || typeof att !== 'object') return att;

      const url =
        att.fileUrl ??
        att.file_url ??
        att.url ??
        att.payload?.url ??
        att.imageData?.url ??
        att.imageData?.previewUrl ??
        att.image_data?.url ??
        att.image_data?.preview_url ??
        att.audioData?.url ??
        att.audio_data?.url ??
        att.videoData?.url ??
        att.video_data?.url ??
        '';

      const type = (att.type ?? '').toLowerCase();

      const isImage =
        type === 'image' ||
        type === 'photo' ||
        att.image === true ||
        !!att.imageData?.url ||
        !!att.image_data?.url ||
        /\.(heic|heif|jpg|jpeg|png|webp|gif|svg)($|\?)/i.test(url);

      const isVoiceClip = /audioclip|voice[-_]/i.test(`${att.name ?? ''} ${url}`);

      const isAudio =
        !isImage &&
        (isVoiceClip ||
          type === 'audio' ||
          type === 'voice' ||
          att.audio === true ||
          !!att.audioData ||
          !!att.audio_data ||
          /\.(mp3|wav|webm|ogg|m4a|aac|opus)($|\?)/i.test(url));

      const isVideo =
        !isImage &&
        !isAudio &&
        (type === 'video' ||
          att.video === true ||
          !!att.videoData ||
          !!att.video_data ||
          /\.(mp4|mov|avi|wmv|flv|mkv|webm)($|\?)/i.test(url));

      let mimeType = att.mimeType || att.mime_type || '';
      if (!mimeType) {
        if (isImage) mimeType = 'image/jpeg';
        else if (isAudio) mimeType = 'audio/mp4';
        else if (isVideo) mimeType = 'video/mp4';
      }

      let imageData = att.imageData ?? att.image_data;
      if (isImage && !imageData && url) {
        imageData = {
          url: url,
          previewUrl: url,
          renderAsSticker: false,
        };
      }

      return {
        ...att,
        id: att.id ?? `${id}-att-${Math.random().toString(36).substring(2, 9)}`,
        mimeType,
        image: isImage,
        video: isVideo,
        audio: isAudio,
        fileUrl: url,
        imageData,
        size: att.size ?? 0,
      };
    })
    .filter((att: any) => {
      if (!att || typeof att !== 'object') return false;
      const key = att.fileUrl || att.url || att.imageData?.url || att.id;
      if (key) {
        if (seenAttachmentKeys.has(key)) return false;
        seenAttachmentKeys.add(key);
      }
      return true;
    });

  const normalized = {
    ...source,
    id: String(id),
    createdTime,
    message: text,
    from: source.from?.id ? source.from : (source.sender ?? source.from ?? { id: '', name: '' }),
    to: source.to ?? (source.recipient ? [source.recipient] : []),
    sticker: resolvedSticker,
    attachments: normalizedAttachments,
  };

  return Object.assign(new FBMessage(), normalized);
}

export function mapWebsocketEventToFbMessage(messageBody: string): FBMessage | null {
  const parsed = JSON.parse(messageBody) as WsEvent | UnknownObject;
  const payload =
    'type' in parsed && parsed.type === 'NEW_MESSAGE'
      ? (parsed as WsEvent).payload
      : parsed;

  return normalizeFbMessage(payload);
}
